import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ArrowLeft, Sparkles, UserPlus, UserMinus, Play, Eye, Users, Video } from 'lucide-react';
import { Button } from './ui/button';
import { API, useAuth } from '../context/AuthContext';
import { useLang } from '../context/LangContext';
import { toast } from '../hooks/use-toast';

const UserProfile = () => {
  const { id } = useParams();
  const { lang } = useLang();
  const { user, login } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);
  const [followers, setFollowers] = useState(0);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const [p, s] = await Promise.all([
        axios.get(`${API}/users/${id}`),
        axios.get(`${API}/follow/status/${id}`, { withCredentials: true })
      ]);
      setProfile(p.data);
      setFollowing(s.data.following);
      setFollowers(s.data.followers);
    } catch (e) { setError(e.response?.data?.detail || 'Not found'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const toggle = async () => {
    if (!user) { login(); return; }
    try {
      if (following) {
        await axios.delete(`${API}/follow/${id}`, { withCredentials: true });
        setFollowing(false); setFollowers(f => Math.max(0, f - 1));
        toast({ title: lang === 'sv' ? 'Slutade följa' : 'تم إلغاء المتابعة' });
      } else {
        await axios.post(`${API}/follow/${id}`, {}, { withCredentials: true });
        setFollowing(true); setFollowers(f => f + 1);
        toast({ title: lang === 'sv' ? 'Följer nu!' : 'أنت الآن تتابع!' });
      }
    } catch (_) {}
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-white"><Loader2 className="w-8 h-8 animate-spin text-cyan-500" /></div>;
  if (error || !profile) return <div className="min-h-screen flex items-center justify-center bg-white text-neutral-500">{lang === 'sv' ? 'Användaren kunde inte hittas' : 'المستخدم غير موجود'}</div>;

  const u = profile.user;
  const isSelf = user?.user_id === u.user_id;

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-10">
        <div className="flex items-center justify-between mb-8">
          <a href="/" className="inline-flex items-center gap-2 text-neutral-500 hover:text-neutral-900 text-[13.5px] font-semibold"><ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {lang === 'sv' ? 'Startsidan' : 'الرئيسية'}</a>
          <div className="flex items-center gap-2"><div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-400 to-fuchsia-500 flex items-center justify-center"><Sparkles className="w-4 h-4 text-white" /></div><span className="font-black text-neutral-900 text-[16px]">CapCute</span></div>
        </div>

        <div className="bg-white rounded-3xl border border-neutral-200 p-8 mb-8 flex flex-col md:flex-row items-start md:items-center gap-6">
          {u.picture ? <img src={u.picture} alt={u.name} className="w-24 h-24 rounded-2xl object-cover" referrerPolicy="no-referrer" /> : <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-cyan-400 to-fuchsia-500 flex items-center justify-center text-white text-3xl font-black">{u.name?.[0] || 'U'}</div>}
          <div className="flex-1">
            <h1 className="text-3xl font-black tracking-tight text-neutral-900">{u.name}</h1>
            <div className="mt-3 flex items-center gap-5 text-[13.5px] text-neutral-700">
              <span className="flex items-center gap-1.5"><Video className="w-4 h-4" /><b>{profile.videos.length}</b> {lang === 'sv' ? 'videor' : 'فيديو'}</span>
              <span className="flex items-center gap-1.5"><Users className="w-4 h-4" /><b>{followers}</b> {lang === 'sv' ? 'följare' : 'متابع'}</span>
              <span className="flex items-center gap-1.5"><b>{profile.following}</b> {lang === 'sv' ? 'följer' : 'يتابع'}</span>
            </div>
          </div>
          {!isSelf && (
            <Button onClick={toggle} className={`h-12 px-6 rounded-full font-bold ${following ? 'bg-neutral-100 hover:bg-neutral-200 text-neutral-900' : 'bg-black hover:bg-neutral-800 text-white'}`}>
              {following ? <><UserMinus className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2" /> {lang === 'sv' ? 'Följer' : 'يتابع'}</> : <><UserPlus className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2" /> {lang === 'sv' ? 'Följ' : 'تابع'}</>}
            </Button>
          )}
        </div>

        {profile.videos.length === 0 ? (
          <div className="bg-white rounded-3xl border border-neutral-200 p-14 text-center"><p className="text-neutral-500">{lang === 'sv' ? 'Inga publika videor än' : 'لا توجد فيديوهات عامة'}</p></div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {profile.videos.map(v => (
              <a key={v.id} href={`/v/${v.id}`} className="group relative bg-white rounded-2xl overflow-hidden border border-neutral-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 aspect-[4/5]">
                {v.thumb_path ? <img src={`${API}/files/${v.thumb_path}`} alt={v.title} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-cyan-500 to-fuchsia-500" />}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center"><Play className="w-5 h-5 fill-black text-black ml-0.5" /></div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <div className="font-bold text-white text-[13px] truncate">{v.title}</div>
                  <div className="text-white/80 text-[11px] font-semibold flex items-center gap-1 mt-1"><Eye className="w-3 h-3" /> {v.view_count || 0}</div>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile;
